import React, { FC, Fragment, ChangeEvent, useCallback, useState, useMemo } from 'react';
import styled from 'styled-components';
import { Page } from '../../ui/layout/page';
import { Header } from '../../ui/layout/header';
import { Section } from '../../ui/layout/section';
import { Fieldset } from '../../ui/form/fieldset';
import { Input } from '../../ui/form/input';
import { Select } from '../../ui/form/select';
//import { SelectFiltered } from '../../ui/form/select-filtered';
import { Footnote } from '../../ui/typography';
import { Button } from '../../ui/buttons/button';
import { ButtonNextIcon, ButtonRestartIcon } from '../../ui/icons';
import { Motivation, Warning } from '../../ui/typography';
import { IChildFormData } from '@app/state/types/model';
import { supportGameMap, getSortedLanguageOptions, OptionsList } from '@app/state/types';
import { StyledForm, TwoColFieldset } from './partials/styled-form';
import { ChildcodeTool } from './partials/childcode-tool';

export interface IViewProps {
  formValues: IChildFormData;
  done: boolean;
  facilityLocked: boolean;
  teacherLocked: boolean;
  facilities: string[];
  onSubmit: (data: Partial<IChildFormData>) => void;
  onNext: () => void;
  onNewChild: () => void;
}

export const View: FC<IViewProps> = ({
  formValues,
  done,
  facilities,
  facilityLocked,
  onSubmit,
  onNext,
  onNewChild,
}) => {
  const [langOptions] = useState<OptionsList[]>(getSortedLanguageOptions());
  // compute quarters between minBirth and maxBirth
  const [birthQuarters] = useState<OptionsList[]>(() => {
    const result: OptionsList[] = [];
    const minBirth = new Date(new Date().getFullYear() - 13, new Date().getMonth(), new Date().getDate());
    const maxBirth = new Date(new Date().getFullYear() - 3, new Date().getMonth(), new Date().getDate());

    // start from the first day of minBirth's quarter
    let year = minBirth.getFullYear();
    let quarterStartMonth = Math.floor(minBirth.getMonth() / 3) * 3;

    let date = new Date(year, quarterStartMonth, 1);

    while (date <= maxBirth) {
      const qNum = Math.floor(date.getMonth() / 3) + 1;
      const month = (date.getMonth() + 1).toString().padStart(2, '0'); // MM
      const label = `Q${qNum}-${date.getFullYear()}`;
      const value = `${date.getFullYear()}-${month}-01`; // e.g. "2023-04-01"
      result.push({ label, value });

      // move to next quarter
      date = new Date(date.getFullYear(), date.getMonth() + 3, 1);
    }

    return result.reverse();
  });
  const [testingMonths] = useState<OptionsList[]>(() => {
    const result: OptionsList[] = [];

    const now = new Date();
    const minDate = new Date(now.getFullYear() - 2, now.getMonth(), 1); // 2 years back, same month
    const maxDate = new Date(now.getFullYear(), now.getMonth(), 1); // current month start

    let date = new Date(minDate);

    while (date <= maxDate) {
      const month = (date.getMonth() + 1).toString().padStart(2, '0'); // MM
      const year = date.getFullYear(); // YYYY
      const label = `${month}-${year}`;
      const value = `${year}-${month}-01`; // e.g. "2023-04-01"

      result.push({ label, value });

      // move to next month
      date = new Date(date.getFullYear(), date.getMonth() + 1, 1);
    }

    // reverse order: newest first
    return result.reverse();
  });
  const onChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const property = evt.currentTarget.name;
      let value: any = null;
      if (!!evt.currentTarget.value) {
        const type = evt.currentTarget.getAttribute('data-type') || evt.currentTarget.type;
        switch (type) {
          case 'number':
            value = Number(evt.currentTarget.value);
            if (isNaN(value)) return;
            break;
          case 'date':
            value = new Date(evt.currentTarget.value);
            break;
          default:
            value = `${evt.currentTarget.value}`;
        }
      }

      onSubmit({
        [property]: value,
      });
    },
    [onSubmit],
  );

  return (
    <Page header={<Header title="Infos zum Kind erfassen" />}>
      <Section>
        <StyledForm>
          <Fieldset label="Codierung des Kindes">
            <Input
              required
              pattern="\p{Letter}{4}(?:(?:\d{2}[A-Z]{3}[a-z]{3})|(?:\d{3,4}[A-Z]{3}))"
              name="childCode"
              value={formValues.childCode ?? ''}
              onChange={onChange}
            />
          </Fieldset>
          <Fieldset>
            <Button variant="secondary" onClick={onNewChild}>
              <ButtonRestartIcon />
              NEUES KIND / LÖSCHEN
            </Button>
          </Fieldset>
          <Fieldset label="Monat / Jahr der Testdurchführung">
            <Select
              required
              name="examDate"
              data-type="date"
              value={formValues.examDate?.toISOString().substring(0, 10) ?? ''}
              onChange={onChange}
            >
              <option value="">Bitte wählen</option>
              {testingMonths.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
          </Fieldset>
          <Fieldset label="Diagnostik">
            <Select name="diagnosis" required value={formValues.diagnosis ?? ''} onChange={onChange}>
              <option value={undefined}></option>
              <option value="start">Anfangsdiagnostik</option>
              <option value="end">Enddiagnostik</option>
            </Select>
          </Fieldset>
        </StyledForm>
        {formValues.childCode === null && (
          <ChildcodeTool
            key={formValues.childCode}
            updateChildCode={(childCode, dateOfBirth) =>
              onSubmit({
                childCode,
                dateOfBirth,
              })
            }
          />
        )}
        <StyledForm>
          {formValues.diagnosis && (
            <React.Fragment>
              {/* Line 1 */}
              <TwoColFieldset label="Name der Einrichtung">
                {facilityLocked && (
                  <Select
                    required
                    disabled={facilityLocked}
                    name="nameOfInstitute"
                    value={formValues.nameOfInstitute ?? ''}
                    onChange={onChange}
                  >
                    {facilities &&
                      facilities.map((facility) => (
                        <option key={facility} value={facility}>
                          {facility}
                        </option>
                      ))}
                  </Select>
                )}
                {!facilityLocked && (
                  <Input
                    required
                    disabled={facilityLocked}
                    name="nameOfInstitute"
                    value={formValues.nameOfInstitute ?? ''}
                    onChange={onChange}
                  />
                )}
              </TwoColFieldset>

              {/* Line 2 */}
              <Fieldset label="Geburtsquartal / -jahr des Kindes">
                <Select
                  required
                  name="dateOfBirth"
                  data-type="date"
                  value={formValues.dateOfBirth?.toISOString().substring(0, 10) ?? ''}
                  onChange={onChange}
                >
                  <option value="">Bitte wählen</option>
                  {birthQuarters.map((q) => (
                    <option key={q.value} value={q.value}>
                      {q.label}
                    </option>
                  ))}
                </Select>
              </Fieldset>
              <Fieldset label="Geschlecht">
                <Select required name="gender" value={formValues.gender ?? ''} onChange={onChange}>
                  <option value={undefined}></option>
                  <option value="male">Männlich</option>
                  <option value="female">Weiblich</option>
                  <option value="diverse">Unbekannt</option>
                </Select>
              </Fieldset>
              <Fieldset label="Herkunftssprache">
                <Select name="nativeLanguage" required value={formValues.nativeLanguage ?? ''} onChange={onChange}>
                  <option value={undefined}></option>
                  {langOptions.map((lo: OptionsList) => (
                    <option key={lo.value} value={lo.value}>
                      {lo.label}
                    </option>
                  ))}
                </Select>
              </Fieldset>
              <Fieldset label="Herkunftssprache 2">
                <Select name="nativeLanguage2" value={formValues.nativeLanguage2 ?? ''} onChange={onChange}>
                  <option value={undefined}></option>
                  {langOptions.map((lo: OptionsList) => (
                    <option key={lo.value} value={lo.value}>
                      {lo.label}
                    </option>
                  ))}
                </Select>
              </Fieldset>

              {/* Line 3 */}
              <Fieldset label="Klassenstufe">
                <Select name="classLevel" required value={formValues.classLevel ?? ''} onChange={onChange}>
                  <option value={undefined}></option>
                  <option value="daycare">Kita</option>
                  <option value="pre-school">Vorschule</option>
                  <option value="class-1">Klassenstufe 1</option>
                  <option value="class-2">Klassenstufe 2</option>
                  <option value="class-3">Klassenstufe 3</option>
                </Select>
              </Fieldset>

              {formValues.diagnosis && formValues.diagnosis === 'end' && (
                <React.Fragment>
                  <Fieldset label="Name der MITsprache-Gruppe">
                    <Input
                      name="classDesignation"
                      required
                      onChange={onChange}
                      value={formValues.classDesignation ?? ''}
                    />
                  </Fieldset>
                  <div />
                  <div />

                  {/* Line 4 */}
                  <Fieldset label="Förderstufe" hint="(Förderfarbe)">
                    <Select name="supportLevel" required value={formValues.supportLevel ?? ''} onChange={onChange}>
                      <option value={undefined}></option>
                      <option value="purple">Lila</option>
                      <option value="blue">Blau</option>
                      <option value="orange">Orange</option>
                      <option value="red">Rot</option>
                      <option value="green">Grün</option>
                    </Select>
                  </Fieldset>
                  <Fieldset label="Förderspielnummer">
                    <Select
                      disabled={!formValues.supportLevel}
                      name="supportGame"
                      value={formValues.supportGame ?? ''}
                      onChange={onChange}
                    >
                      <option value={undefined}></option>
                      {formValues.supportLevel &&
                        supportGameMap[formValues.supportLevel].map((supportGameOption) => (
                          <option key={supportGameOption} value={supportGameOption}>
                            {supportGameOption}
                          </option>
                        ))}
                    </Select>
                  </Fieldset>
                </React.Fragment>
              )}
            </React.Fragment>
          )}
        </StyledForm>
      </Section>
      <Section variant="done">
        <Button variant="primary" onClick={onNext} disabled={!done}>
          <ButtonNextIcon />
          WEITER
        </Button>
        {done ? <Motivation>Alles erfasst!</Motivation> : <Warning>Bitte füllen Sie alles aus!</Warning>}
      </Section>
    </Page>
  );
};
